'use client';

import React, { useState, useMemo } from 'react';
import { Card, Text } from '@tremor/react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRoot,
  TableRow,
} from '@/components/molecules/Table';
import { ITableData } from './types';
import { parseValue } from './utils';
import { Button } from '@/components/atoms/Button';
import { useWidgetFiltersController } from '../WidgetFilters/useWidgetFiltersController';
import { NoDataView, FilteredNoDataView } from '../NoDataView';

interface CompetitorTableProps {
  data: ITableData;
  widgetFiltersController?: ReturnType<typeof useWidgetFiltersController>;
  filterDimension?: string;
  clientName?: string;
  pageLimit?: number;
  hideHeader?: boolean;
}

interface CompetitorData {
  rank: number | string;
  change: number | string;
}

interface KeywordRow {
  keyword: string;
  competitors: Record<string, CompetitorData>;
}

export function CompetitorTable({
  data: _data,
  widgetFiltersController,
  filterDimension,
  clientName,
  pageLimit: pageLimitProp,
  hideHeader,
}: CompetitorTableProps) {
  const [sortField, setSortField] = useState(_data?.sortBy);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(_data?.sortDirection || 'asc');
  const pageLimit = pageLimitProp || 15;
  const [displayedRowsCount, setDisplayedRowsCount] = useState(pageLimit);

  const data = useMemo(() => {
    let processedRows = _data?.rows || [];

    if (widgetFiltersController && processedRows.length > 0 && (widgetFiltersController.dimensionFilterActive || widgetFiltersController.selectedDimensionItems.length > 0)) {
      processedRows = processedRows.filter(row => {
        const dimensionValue = row[filterDimension ?? ''];
        return widgetFiltersController.selectedDimensionItems.some((item) => dimensionValue == item.text);
      });
    }

    return {
      ..._data,
      rows: processedRows
    };
  }, [_data, widgetFiltersController?.selectedDimensionItems, widgetFiltersController?.dimensionFilterActive]);

  const transformedData = useMemo(() => {
    if (!data?.rows || !Array.isArray(data.rows)) {
      return { keywords: [], competitors: [] };
    }
    const keywordMap = new Map<string, KeywordRow>();
    const competitorsSet = new Set<string>();
    const competitorOrder: string[] = [];

    // Process each competitor row (segmentValue) and their expansion data
    data.rows.forEach(row => {
      const competitor = row.segmentValue || row.segment || '';
      if (!competitor) return;

      // Track competitor order
      if (!competitorsSet.has(competitor)) {
        competitorsSet.add(competitor);
        competitorOrder.push(competitor);
      }

      // Check if this row has expansion data with keywords
      const expansion = row.expansion;
      if (!expansion || !expansion.rows || !Array.isArray(expansion.rows)) return;

      // Get the breakdown field (usually KeywordName)
      const breakdownBy = expansion.metadata?.breakdownBy || 'KeywordName';

      // Process each keyword in the expansion
      expansion.rows.forEach((keywordRow: any) => {
        const keyword = keywordRow[breakdownBy] || '';
        if (!keyword) return;

        const rank = keywordRow.pageRank ?? keywordRow.rank ?? '-';
        const prevRank = keywordRow.prevMonthPageRank ?? keywordRow.prevRank ?? '-';
        
        // Calculate change (current - previous, negative is better for rankings)
        let change: number | string = '-';
        if (typeof rank === 'number' && typeof prevRank === 'number') {
          change = rank - prevRank;
        }

        // Initialize keyword row if it doesn't exist
        if (!keywordMap.has(keyword)) {
          keywordMap.set(keyword, {
            keyword,
            competitors: {}
          });
        }

        const keywordRowData = keywordMap.get(keyword)!;
        keywordRowData.competitors[competitor] = { rank, change };
      });
    });

    return {
      keywords: Array.from(keywordMap.values()),
      competitors: competitorOrder
    };
  }, [_data, widgetFiltersController?.selectedDimensionItems, filterDimension]);

  const { keywords, competitors } = transformedData;

  // Determine client name from data or prop
  const detectedClientName = useMemo(() => {
    if (clientName) return clientName;
    
    // Try to detect from clientName field in expansion rows
    if (data?.rows && data.rows.length > 0) {
      const firstRow = data.rows[0];
      if (firstRow.expansion?.rows?.[0]?.clientName) {
        return firstRow.expansion.rows[0].clientName;
      }
    }
    
    // Fallback to first competitor
    return competitors[0] || '';
  }, [clientName, competitors, data?.rows]);

  const sortedKeywords = useMemo(() => {
    if (!sortField) return keywords;

    return [...keywords].sort((a, b) => {
      // Sort by keyword name
      if (sortField === 'keyword') {
        const aVal = a.keyword.toLowerCase();
        const bVal = b.keyword.toLowerCase();
        return sortDir === 'asc' ? 
          (aVal > bVal ? 1 : -1) : 
          (aVal < bVal ? 1 : -1);
      }

      // Sort by specific competitor's rank
      const aRank = a.competitors[sortField]?.rank;
      const bRank = b.competitors[sortField]?.rank;

      if (aRank === undefined && bRank === undefined) return 0;
      if (aRank === undefined || aRank === '-') return 1;
      if (bRank === undefined || bRank === '-') return -1;

      const aParsed = parseValue(aRank);
      const bParsed = parseValue(bRank);

      if (aParsed.isNumeric && bParsed.isNumeric) {
        return sortDir === 'asc' ? 
          aParsed.numValue - bParsed.numValue : 
          bParsed.numValue - aParsed.numValue;
      }

      return 0;
    });
  }, [keywords, sortField, sortDir]);

  if (!data) {
    return (
      <Card className="w-full p-4 text-center">
        <Text>No table data available</Text>
      </Card>
    );
  }

  if (!data.columns || !Array.isArray(data.columns) || data.columns.length === 0) {
    return (
      <Card className="w-full p-4 text-center">
        <Text>Invalid table structure: missing columns</Text>
      </Card>
    );
  }

  if (!data.rows || !Array.isArray(data.rows)) {
    return (
      <Card className="w-full p-4 text-center">
        <Text>Invalid table structure: missing rows</Text>
      </Card>
    );
  }

  const totalRows = sortedKeywords.length;
  const displayedRows = sortedKeywords.slice(0, displayedRowsCount);

  const handleSort = (field: string) => {
    const newDir = field === sortField && sortDir === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDir(newDir);
    setDisplayedRowsCount(pageLimit);
  };

  const handleLoadMore = () => {
    setDisplayedRowsCount(prev => Math.min(prev + pageLimit, totalRows));
  };

  const handleCollapse = () => {
    setDisplayedRowsCount(pageLimit);
  };

  const isFullyLoaded = displayedRowsCount >= totalRows;

  // If no keywords to display, show NoDataView instead of empty table. When a dimension/campaign
  // filter is the reason there are zero rows, show the filter-specific message (LAD-454).
  if (sortedKeywords.length === 0) {
    const filteredEmpty = !!widgetFiltersController && (widgetFiltersController.dimensionFilterActive || widgetFiltersController.selectedDimensionItems.length > 0);
    return filteredEmpty ? <FilteredNoDataView /> : <NoDataView />;
  }

  const formatValue = (value: any) => {
    if (value === null || value === undefined || value === '' || value === '-') {
      return '-';
    }
    return String(value);
  };

  const formatChange = (value: any) => {
    if (value === null || value === undefined || value === '' || value === '-') {
      return '-';
    }

    // Handle numeric values directly
    if (typeof value === 'number') {
      if (value === 0) {
        return '-';
      } else if (value > 0) {
        // Positive change = rank got worse (higher number)
        return <span style={{ color: '#dc2626' }}>↓ {value}</span>;
      } else {
        // Negative change = rank got better (lower number)
        return <span style={{ color: '#16a34a' }}>↑ {Math.abs(value)}</span>;
      }
    }

    const str = String(value);
    const parsed = parseValue(str);

    if (parsed.isNumeric) {
      const num = parsed.numValue;
      if (num === 0) {
        return '-';
      } else if (num > 0) {
        return <span style={{ color: '#dc2626' }}>↓ {num}</span>;
      } else {
        return <span style={{ color: '#16a34a' }}>↑ {Math.abs(num)}</span>;
      }
    }

    // Check if string already has arrow
    if (str.includes('↑') || str.includes('↓') || str.includes('+') || str.includes('-')) {
      const isPositive = str.includes('↑') || str.includes('+');
      const cleanValue = str.replace(/[↑↓+\-\s]/g, '');
      return (
        <span style={{ color: isPositive ? '#16a34a' : '#dc2626' }}>
          {isPositive ? '↑' : '↓'} {cleanValue}
        </span>
      );
    }

    return str;
  };

  return (
    <Card className="w-full h-full">
      <TableRoot className="overflow-x-auto">
        <Table className="border-collapse">
          {!hideHeader && <TableHead>
            {/* First header row - competitor names */}
            <TableRow>
              <TableHeaderCell
                rowSpan={2}
                onClick={() => handleSort('keyword')}
                className="sticky left-0 bg-[#f9f9f9] z-10 shadow-[inset_-1px_0_0_0_rgb(209,213,219)] cursor-pointer"
                style={{
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  color: '#232F34',
                  textTransform: 'uppercase',
                  fontWeight: 'bold',
                  textAlign: 'center',
                }}
              >
                <div className="flex items-center justify-center">
                  KEYWORD
                  {sortField === 'keyword' && (
                    <span className="ml-2 text-xs">
                      {sortDir === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </div>
              </TableHeaderCell>

              {competitors.map((competitor, index) => {
                const isClient = competitor.toLowerCase().includes(detectedClientName.toLowerCase()) || 
                                competitor === detectedClientName;
                const isLast = index === competitors.length - 1;
                
                return (
                  <TableHeaderCell
                    key={competitor}
                    colSpan={2}
                    onClick={() => handleSort(competitor)}
                    className={`cursor-pointer ${isLast ? '!border-r-0' : ''} ${
                      isClient ? 'bg-[#e8f4f3]' : 'bg-[#f9f9f9]'
                    }`}
                    style={{
                      fontFamily: 'Roboto, sans-serif',
                      fontSize: '14px',
                      color: '#232F34',
                      textTransform: 'uppercase',
                      fontWeight: 'bold',
                      textAlign: 'center',
                    }}
                  >
                    <div className="flex items-center justify-center">
                      {competitor}
                      {sortField === competitor && (
                        <span className="ml-2 text-xs">
                          {sortDir === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </TableHeaderCell>
                );
              })}
            </TableRow>

            {/* Second header row - RANK and CHANGE */}
            <TableRow>
              {competitors.map((competitor, index) => {
                const isClient = competitor.toLowerCase().includes(detectedClientName.toLowerCase()) || 
                                competitor === detectedClientName;
                const isLast = index === competitors.length - 1;

                return (
                  <React.Fragment key={`sub-${competitor}`}>
                    <TableHeaderCell
                      className={isClient ? 'bg-[#e8f4f3]' : 'bg-[#f9f9f9]'}
                      style={{
                        fontFamily: 'Roboto, sans-serif',
                        fontSize: '14px',
                        color: '#232F34',
                        fontWeight: 'bold',
                        textAlign: 'center',
                      }}
                    >
                      RANK
                    </TableHeaderCell>
                    <TableHeaderCell
                      className={`${isLast ? '!border-r-0' : ''} ${
                        isClient ? 'bg-[#e8f4f3]' : 'bg-[#f9f9f9]'
                      }`}
                      style={{
                        fontFamily: 'Roboto, sans-serif',
                        fontSize: '14px',
                        color: '#232F34',
                        fontWeight: 'bold',
                        textAlign: 'center',
                      }}
                    >
                      CHANGE
                    </TableHeaderCell>
                  </React.Fragment>
                );
              })}
            </TableRow>
          </TableHead>}

          <TableBody>
            {displayedRows.length > 0 ? (
              displayedRows.map((keywordRow, rowIndex) => {
                const isLastDisplayedRow = rowIndex === displayedRows.length - 1;

                return (
                  <TableRow key={keywordRow.keyword} className="hover:bg-gray-50">
                    <TableCell
                      className="sticky left-0 bg-white z-5 shadow-[inset_-1px_0_0_0_rgb(209,213,219)] text-left"
                      isFirstColumn={true}
                      isLastColumn={false}
                      isLastRow={isLastDisplayedRow}
                    >
                      {keywordRow.keyword}
                    </TableCell>

                    {competitors.map((competitor, compIndex) => {
                      const competitorData = keywordRow.competitors[competitor] || { rank: '-', change: '-' };
                      const isClient = competitor.toLowerCase().includes(detectedClientName.toLowerCase()) || 
                                      competitor === detectedClientName;
                      const isLastCompetitor = compIndex === competitors.length - 1;

                      return (
                        <React.Fragment key={`${keywordRow.keyword}-${competitor}`}>
                          <TableCell
                            className={`text-center ${isClient ? 'bg-[#f5fafa]' : ''}`}
                            isFirstColumn={false}
                            isLastColumn={false}
                            isLastRow={isLastDisplayedRow}
                          >
                            {formatValue(competitorData.rank)}
                          </TableCell>
                          <TableCell
                            className={`text-center ${isClient ? 'bg-[#f5fafa]' : ''}`}
                            isFirstColumn={false}
                            isLastColumn={isLastCompetitor}
                            isLastRow={isLastDisplayedRow}
                          >
                            {formatChange(competitorData.change)}
                          </TableCell>
                        </React.Fragment>
                      );
                    })}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={1 + competitors.length * 2} className="text-center">
                  No data available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableRoot>

      {/* Load More Controls */}
      {totalRows > pageLimit && (
        <div className="relative flex items-center mt-4 pt-4">
          <div className="text-sm text-gray-500">
            Showing 1-{displayedRowsCount} of {totalRows}
          </div>
          <div className="absolute left-1/2 transform -translate-x-1/2 flex gap-2">
            <Button
              variant="outline"
              className="px-6 py-2.5"
              onClick={handleLoadMore}
              disabled={isFullyLoaded}
            >
              Load More
            </Button>
            {displayedRowsCount > pageLimit && (
              <Button
                variant="outline"
                className="px-6 py-2.5"
                onClick={handleCollapse}
              >
                Collapse to Default
              </Button>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}
